import SendIcon from '@mui/icons-material/Send'
import SendIconOutlined from '@mui/icons-material/SendOutlined'
import { Grid, IconButton, TextField, useTheme } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useState } from 'react'
import { Prompt } from 'react-router-dom'
import { useNonprofitContext } from "../../hooks/commonOrgContext"
import { useTeamRoleContext } from "../../hooks/teamRoleContext"
import { useScheduledActivityContext } from '../../hooks/useScheduledActivityContext'
import { useScheduledActivityMessagesContext } from '../../hooks/useScheduledActivityMessagesContext'
import preventNav from '../../util/preventNav'

interface Props {
  label: string
  autoFocus: boolean
  parentMessageId?: string
  createMessage: (messageBody: string, parentMessageId?: string, orgUnitId?: string) => Promise<boolean>
  orgUnitId?: string,
}

export default function ScheduledActivityMessageForm({
  label,
  autoFocus,
  parentMessageId,
  createMessage,
  orgUnitId,
}:Props) {
  const [messageBody, setMessageBody] = useState("")
  const [loading, setLoading] = useState(false)
  const [allowPrompt, setAllowPrompt] = useState(true)
  const formIsDirty = !!messageBody && allowPrompt
  const theme = useTheme()
  const { teamOwned } = useScheduledActivityContext()

  const {
    pauseMessagesSubscription,
    resumeMessagesSubscription,
  } = useScheduledActivityMessagesContext()

  const handleSubmit = async () => {
    setLoading(true)
    setAllowPrompt(false)
    const success = await createMessage(messageBody, parentMessageId, orgUnitId)
    if (success) {
      setMessageBody("")
    }
    setLoading(false)
  }

  const { canCoordinate, npo } = useNonprofitContext()
  const { canCoordinate: canCoordinateTeam } = useTeamRoleContext()

  const coordinatorHelpText = "Share a message with volunteers and coordinators. \
                              To communicate directly with coordinators, head to the Field Trip plan for this event."
  const nonCoordinatorHelpText = "Share a message with volunteers and coordinators."
  const teamMessageHelpText = "Share messsage with your Team members. \
                              Other registered users will not be able to view your Team messages."
  const managedFieldTripHelpText = "Share a message with volunteers."
  const helpTextByCoordinatingType = (() => {
    if (teamOwned) return managedFieldTripHelpText
    if (canCoordinate() || canCoordinateTeam()) return coordinatorHelpText
    return nonCoordinatorHelpText
  })()
  const messageHelpText = orgUnitId ? teamMessageHelpText : helpTextByCoordinatingType

  preventNav(formIsDirty)

  return (
    <>
      <Prompt when={formIsDirty} message={"Looks like you have an unsent message. If you leave this page you will lose your changes"}/>
      <Grid
        container
        direction="row"
        spacing={1}
        sx={{ pt: 1 }}
      >
        <Grid item xs={10.5} sx={{ mb: -1 }}>
          <TextField
            multiline
            label={label}
            onChange={(e) => setMessageBody(e.target.value)}
            maxRows={8}
            fullWidth
            value={messageBody}
            autoFocus={autoFocus}
            onFocus={ pauseMessagesSubscription }
            onBlur={ resumeMessagesSubscription }
            helperText={messageHelpText}
          />
        </Grid>
        <Grid item xs={1} style={{ marginTop: 8, alignSelf: "start", color: theme.palette.neutral.main }}>
          {!loading && (
            <IconButton
              color={!messageBody ? "inherit" : "primary"}
              onClick={ handleSubmit }
            >
              {!messageBody ? <SendIconOutlined /> : <SendIcon />}
            </IconButton>
          )}
          {loading && (
            <CircularProgress size={ 30 } />
          )}
        </Grid>
      </Grid>
    </>
  )
}
